import { startOfDay, subDays } from "date-fns";
import { TZDate } from "@date-fns/tz";
/**
 * Formato de dinero, cantidades y fechas.
 *
 * Dos reglas del proyecto viven aqui:
 *
 *   1. `numeric` llega de PostgreSQL como string para no perder precision.
 *      Estas funciones lo formatean; nadie lo suma en JavaScript. Todo total
 *      lo calcula la base.
 *
 *   2. Los timestamps se guardan en UTC y se muestran en la zona horaria de
 *      la organizacion. Un `toLocaleString()` suelto en un componente produce
 *      pedidos con fecha de ayer en el panel de hoy.
 */

export function formatMoney(
  value: string | number | null | undefined,
  currency = "PEN",
  { withSymbol = true }: { withSymbol?: boolean } = {},
): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";

  const amount = new Intl.NumberFormat("es-PE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

  if (!withSymbol) return amount;
  return `${currency === "PEN" ? "S/" : currency} ${amount}`;
}

export function formatQty(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("es-PE").format(value);
}

export function formatDateTime(
  iso: string | null | undefined,
  timeZone = "America/Lima",
): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-PE", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  }).format(new Date(iso));
}

export function formatTime(
  iso: string | null | undefined,
  timeZone = "America/Lima",
): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-PE", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  }).format(new Date(iso));
}

/**
 * Instante en el que empezó «hoy» para el comercio, en ISO.
 *
 * «Ventas de hoy» no es `created_at >= current_date`: eso usaría la zona del
 * servidor y a las 19:00 de Lima ya estaría contando el día siguiente. El día
 * de negocio empieza a medianoche en la zona horaria de la organización.
 */
/**
 * Medianoche de hoy en la zona de la tienda, en ISO.
 *
 * Estaba resuelto a mano reconstruyendo el desfase desde Intl. Funcionaba,
 * pero solo porque Peru no cambia de hora: en una tienda con horario de
 * verano el desfase de hoy no es el de ayer, y restar milisegundos parte el
 * dia una hora antes o despues dos veces al año.
 */
export function startOfDayISO(timeZone = "America/Lima"): string {
  return startOfDay(new TZDate(Date.now(), timeZone)).toISOString();
}

/** El mismo instante de hace N dias, respetando el calendario de la zona. */
export function diasAntes(dias: number, timeZone = "America/Lima"): Date {
  return subDays(new TZDate(Date.now(), timeZone), dias);
}

/** Medianoche de hace N dias en la zona de la tienda. */
export function inicioDeDiaHace(dias: number, timeZone = "America/Lima"): Date {
  return startOfDay(subDays(new TZDate(Date.now(), timeZone), dias));
}

/** «hoy», «ayer» o la fecha. Para agrupar listas de pedidos. */
export function formatDayLabel(
  iso: string | null | undefined,
  timeZone = "America/Lima",
): string {
  if (!iso) return "—";
  const fmt = (d: Date) =>
    new Intl.DateTimeFormat("en-CA", { timeZone, dateStyle: "short" }).format(d);

  const target = fmt(new Date(iso));
  const today = fmt(new Date());
  const yesterday = fmt(subDays(new TZDate(Date.now(), timeZone), 1));

  if (target === today) return "Hoy";
  if (target === yesterday) return "Ayer";

  return new Intl.DateTimeFormat("es-PE", {
    day: "numeric",
    month: "long",
    timeZone,
  }).format(new Date(iso));
}
